"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VIEWPORT_MD } from "~/constants";
import { useFindPlace } from "~/hooks/addresses";
import { useLocations } from "~/hooks/locations";
import { LoaderIcon } from "~/icons";
import type { Coords, RichJetsLocation } from "~/types";
import type { JetsLocationWithRef } from "~/utils/jets-locations";
import { Box } from "../Box/Box";
import { Divider } from "../Divider/Divider";
import { LocationCard } from "../LocationCard/LocationCard";
import { ModalHeader } from "../Modal/Modal";
import { Text } from "../Text/Text";
import { TextField } from "../TextField/TextField";

const US_STATES = [
  { stateName: "Alabama", stateAbbreviation: "AL" },
  { stateName: "Alaska", stateAbbreviation: "AK" },
  { stateName: "Arizona", stateAbbreviation: "AZ" },
  { stateName: "Arkansas", stateAbbreviation: "AR" },
  { stateName: "California", stateAbbreviation: "CA" },
  { stateName: "Colorado", stateAbbreviation: "CO" },
  { stateName: "Connecticut", stateAbbreviation: "CT" },
  { stateName: "Delaware", stateAbbreviation: "DE" },
  { stateName: "Florida", stateAbbreviation: "FL" },
  { stateName: "Georgia", stateAbbreviation: "GA" },
  { stateName: "Hawaii", stateAbbreviation: "HI" },
  { stateName: "Idaho", stateAbbreviation: "ID" },
  { stateName: "Illinois", stateAbbreviation: "IL" },
  { stateName: "Indiana", stateAbbreviation: "IN" },
  { stateName: "Iowa", stateAbbreviation: "IA" },
  { stateName: "Kansas", stateAbbreviation: "KS" },
  { stateName: "Kentucky", stateAbbreviation: "KY" },
  { stateName: "Louisiana", stateAbbreviation: "LA" },
  { stateName: "Maine", stateAbbreviation: "ME" },
  { stateName: "Maryland", stateAbbreviation: "MD" },
  { stateName: "Massachusetts", stateAbbreviation: "MA" },
  { stateName: "Michigan", stateAbbreviation: "MI" },
  { stateName: "Minnesota", stateAbbreviation: "MN" },
  { stateName: "Mississippi", stateAbbreviation: "MS" },
  { stateName: "Missouri", stateAbbreviation: "MO" },
  { stateName: "Montana", stateAbbreviation: "MT" },
  { stateName: "Nebraska", stateAbbreviation: "NE" },
  { stateName: "Nevada", stateAbbreviation: "NV" },
  { stateName: "New Hampshire", stateAbbreviation: "NH" },
  { stateName: "New Jersey", stateAbbreviation: "NJ" },
  { stateName: "New Mexico", stateAbbreviation: "NM" },
  { stateName: "New York", stateAbbreviation: "NY" },
  { stateName: "North Carolina", stateAbbreviation: "NC" },
  { stateName: "North Dakota", stateAbbreviation: "ND" },
  { stateName: "Ohio", stateAbbreviation: "OH" },
  { stateName: "Oklahoma", stateAbbreviation: "OK" },
  { stateName: "Oregon", stateAbbreviation: "OR" },
  { stateName: "Pennsylvania", stateAbbreviation: "PA" },
  { stateName: "Rhode Island", stateAbbreviation: "RI" },
  { stateName: "South Carolina", stateAbbreviation: "SC" },
  { stateName: "South Dakota", stateAbbreviation: "SD" },
  { stateName: "Tennessee", stateAbbreviation: "TN" },
  { stateName: "Texas", stateAbbreviation: "TX" },
  { stateName: "Utah", stateAbbreviation: "UT" },
  { stateName: "Vermont", stateAbbreviation: "VT" },
  { stateName: "Virginia", stateAbbreviation: "VA" },
  { stateName: "Washington", stateAbbreviation: "WA" },
  { stateName: "West Virginia", stateAbbreviation: "WV" },
  { stateName: "Wisconsin", stateAbbreviation: "WI" },
  { stateName: "Wyoming", stateAbbreviation: "WY" },
] as const;

const MAX_DISTANCE_MILES = 100;

const MAX_LOCATIONS_IN_LIST = 10;

// Production Test locations
const TEST_LOCATION_REFS = [
  "test",
  "test03",
  "test04",
  "hunger02",
  "cec",
  "demo",
];

// Geolocation Error Codes
export const GEOLOCATION_NOT_SUPPORTED = 0;
export const GEOLOCATION_PERMISSION_DENIED = 1;
export const GEOLOCATION_POSITION_UNAVAILABLE = 2;
export const GEOLOCATION_TIMEOUT = 3;

export type LocationSortMode = "geolocate" | "manualSearch";

function locationRefToJetsLocationName(ref: string): string {
  return `${ref.slice(0, 2).toUpperCase()}-${ref.slice(2)}`;
}

// Match a search string against a US state by full name or abbreviation (exact, case-insensitive)
// Returns the matched state object, or null if no match.
function matchUsState(search: string) {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return null;
  return (
    US_STATES.find(
      (state) =>
        state.stateName.toLowerCase() === trimmed ||
        state.stateAbbreviation.toLowerCase() === trimmed,
    ) ?? null
  );
}

// Haversine Formula: https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula
function getDistance(from: Coords, to: Coords): number {
  const radius = 3958.8; // Earth's radius in miles (use 6371 for km)
  const degLat = degToRad(to.latitude - from.latitude);
  const degLng = degToRad(to.longitude - from.longitude);
  const a =
    Math.sin(degLat / 2) * Math.sin(degLat / 2) +
    Math.cos(degToRad(from.latitude)) *
      Math.cos(degToRad(to.latitude)) *
      Math.sin(degLng / 2) *
      Math.sin(degLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radius * c; // distance in miles
}

function degToRad(deg: number) {
  return deg * (Math.PI / 180);
}

function getGeolocationErrorMessage(code: number): string {
  switch (code) {
    case GEOLOCATION_NOT_SUPPORTED:
      return "Geolocation is not supported by your browser. Try searching for a location instead.";
    case GEOLOCATION_PERMISSION_DENIED:
      // Permission may be denied at the browser level or the OS level (e.g. iOS/Android settings).
      // Both result in code 1 — we can't distinguish between them from the error object alone.
      return "Location access denied. Check your browser and OS location permissions, then try again.";
    case GEOLOCATION_POSITION_UNAVAILABLE:
      return "Unable to determine your location. Try searching for a location instead.";
    case GEOLOCATION_TIMEOUT:
      return "Location request timed out. Check your browser and OS location permissions, then try again.";
    default:
      return "Unable to get your location. Try searching for a location instead.";
  }
}

export function LocationSelector({
  jetsLocations,
  initialSortMode,
  initialSearch,
}: {
  jetsLocations: JetsLocationWithRef[];
  initialSortMode: LocationSortMode;
  initialSearch: string;
}) {
  const router = useRouter();

  const [sortMode, setSortMode] = useState<LocationSortMode>(initialSortMode);
  const [inputValue, setInputValue] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [userCoords, setUserCoords] = useState<Coords | undefined>(undefined);
  const [geolocating, setGeolocating] = useState(false);
  const [locationErrorMessage, setLocationErrorMessage] = useState<
    string | null
  >(null);
  const watchIdRef = useRef<number | null>(null);

  const { locations, loading: loadingLocations } = useLocations();

  // A search that exactly matches a US state name/abbreviation is handled as a special
  // "browse by state" case: we skip the findPlace API and filter locations by region instead.
  const stateMatch = useMemo(() => matchUsState(search), [search]);
  const stateSearch = sortMode === "manualSearch" ? stateMatch : null;

  const {
    place,
    loading: loadingFindPlace,
    error: findPlaceError,
  } = useFindPlace(stateMatch ? "" : search);

  const searchDisabled = loadingFindPlace || !inputValue;

  const loading =
    loadingLocations ||
    (loadingFindPlace && sortMode === "manualSearch" && !stateSearch);

  // A manual search that resolved to no place (e.g. gibberish with no "best guess").
  // We treat this the same as "no nearby stores": show the empty state rather than
  // falling back to the full location list with a warning message.
  const placeNotFound =
    sortMode === "manualSearch" &&
    Boolean(search) &&
    !stateSearch &&
    !loadingFindPlace &&
    !place &&
    !findPlaceError;

  // We use watchPosition over getCurrentPosition because Chrome fires watchPosition
  // immediately with any cached position before enforcing maximumAge, giving a fast
  // first result. getCurrentPosition strictly enforces maximumAge and waits for a fresh
  // fix when no valid cache exists. We call clearWatch in both callbacks so only the
  // first position fires — continuous updates aren't needed for a store finder.
  // To switch to getCurrentPosition: same callback signature, but remove clearWatch
  // calls and watchIdRef (no watch ID is returned, so in-flight requests can't be
  // cancelled on unmount).
  const requestGeolocation = useCallback(() => {
    if (watchIdRef.current != null) return; // a watch is already in flight
    if (!navigator.geolocation) {
      setLocationErrorMessage(
        getGeolocationErrorMessage(GEOLOCATION_NOT_SUPPORTED),
      );
    } else {
      setGeolocating(true);
      setLocationErrorMessage(null);
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          navigator.geolocation.clearWatch(watchId);
          watchIdRef.current = null;
          setGeolocating(false);
          setUserCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          navigator.geolocation.clearWatch(watchId);
          watchIdRef.current = null;
          setGeolocating(false);
          setLocationErrorMessage(getGeolocationErrorMessage(error.code));
        },
        { timeout: 10_000, maximumAge: 10 * 60 * 1000 },
      );
      watchIdRef.current = watchId;
    }
  }, []);

  function handleSearchSubmit() {
    const trimmed = inputValue.trim();
    if (!trimmed || searchDisabled) return;
    setInputValue(trimmed);
    setSearch(trimmed);
    setLocationErrorMessage(null);
  }

  // Replaces the URL on mount, so a hard refresh doesn't bring back the initial search params
  // window.history.replaceState() used over router.replace() to avoid Next.js re-render
  useEffect(() => {
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname,
    );
  }, []);

  useEffect(() => {
    if (initialSortMode === "geolocate") requestGeolocation();
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [initialSortMode, requestGeolocation]);

  const jetsRichLocations = useMemo<RichJetsLocation[]>(() => {
    const jetsLocationByRef = new Map(jetsLocations.map((jl) => [jl.ref, jl]));

    // filter out test locations if they are enabled for testing
    const filteredLocations = (locations ?? []).filter(
      (loc) => !TEST_LOCATION_REFS.includes(loc.ref),
    );

    const enrichedLocations = filteredLocations.map((loc) => {
      const jetsLocation = jetsLocationByRef.get(loc.ref);

      return {
        ...loc,
        jetsStoreId: jetsLocation?.id ?? null,
        jetsName: jetsLocation?.name ?? locationRefToJetsLocationName(loc.ref),
        storeLocalReference: jetsLocation?.store_local_reference ?? null,
        userDistance:
          userCoords && loc.coords ? getDistance(userCoords, loc.coords) : null,
        searchDistance:
          place && loc.coords
            ? getDistance(
                { latitude: place.lat, longitude: place.lng },
                loc.coords,
              )
            : null,
      };
    });

    return enrichedLocations;
  }, [locations, jetsLocations, userCoords, place]);

  const filteredSortedLocations = useMemo(() => {
    // No place matched the search — show the empty state instead of the full list.
    if (placeNotFound) return [];

    // Browse-by-state: filter to locations in the matched state, sorted alphabetically by name (not limited to 10 locations).
    if (stateSearch) {
      const region = stateSearch.stateName.toLowerCase();
      return jetsRichLocations
        .filter((loc) => loc.address?.region?.trim().toLowerCase() === region)
        .sort((a, b) => a.name.localeCompare(b.name));
    }

    const hasUserDistances = jetsRichLocations.some(
      (loc) => loc.userDistance != null,
    );

    if (sortMode === "geolocate" && hasUserDistances) {
      return jetsRichLocations
        .filter(
          (loc) =>
            loc.userDistance != null && loc.userDistance <= MAX_DISTANCE_MILES,
        )
        .sort(
          (a, b) => (a.userDistance ?? Infinity) - (b.userDistance ?? Infinity),
        )
        .slice(0, MAX_LOCATIONS_IN_LIST);
    }

    const hasSearchDistances = jetsRichLocations.some(
      (loc) => loc.searchDistance != null,
    );

    if (sortMode === "manualSearch" && hasSearchDistances) {
      return jetsRichLocations
        .filter(
          (loc) =>
            loc.searchDistance != null &&
            loc.searchDistance <= MAX_DISTANCE_MILES,
        )
        .sort(
          (a, b) =>
            (a.searchDistance ?? Infinity) - (b.searchDistance ?? Infinity),
        )
        .slice(0, MAX_LOCATIONS_IN_LIST);
    }

    // Fallback to alphabetical sort while coords are pending or search text is empty
    return [...jetsRichLocations].sort((a, b) => a.name.localeCompare(b.name));
  }, [jetsRichLocations, sortMode, stateSearch, placeNotFound]);

  let foundResultsText = "";
  if (!loading) {
    foundResultsText = `${filteredSortedLocations.length} ${
      filteredSortedLocations.length === 1 ? "Store" : "Stores"
    } found`;
    if (stateSearch) {
      foundResultsText += ` in ${stateSearch.stateName}`;
    } else if (sortMode === "manualSearch" && place?.formattedAddress) {
      foundResultsText += ` near ${place.formattedAddress}`;
    } else if (sortMode === "geolocate" && userCoords) {
      foundResultsText += " near you";
    }
  }

  let noResultsText =
    "No stores found. Try searching for a different location instead.";
  if (placeNotFound) {
    noResultsText = `No location found for "${search}". Try searching for a different location instead.`;
  } else if (stateSearch) {
    noResultsText = `No stores found in ${stateSearch.stateName}. Try searching for a different location instead.`;
  } else if (sortMode === "geolocate") {
    noResultsText = `No stores found within ${MAX_DISTANCE_MILES} miles of you. Try "Find" and search for a location instead.`;
  } else if (place?.formattedAddress) {
    noResultsText = `No stores found within ${MAX_DISTANCE_MILES} miles of ${place.formattedAddress}. Try searching for a different location instead.`;
  }

  return (
    <Box display="flex" flexDirection="column" style={{ minHeight: "100vh" }}>
      <Box position="sticky" style={{ top: 0, zIndex: 1 }}>
        <ModalHeader
          actionType="back"
          onAction={() => router.push("/")}
          title="Find a Store"
          busy={false}
        />
      </Box>
      <Box
        paddingX="2"
        paddingTop="2"
        style={{ maxWidth: VIEWPORT_MD, margin: "0 auto", width: "100%" }}
      >
        <Box
          paddingBottom="2"
          paddingLeft="3"
          display="flex"
          flexDirection="row"
          justifyContent="spaceBetween"
          alignItems="center"
        >
          <Text color="neutral-900" size="standard" weight="regular">
            {foundResultsText}
          </Text>
          <Box display="flex" gap="2" paddingLeft="1">
            <Box
              component="button"
              type="button"
              onClick={() => {
                if (watchIdRef.current != null) {
                  navigator.geolocation.clearWatch(watchIdRef.current);
                  watchIdRef.current = null;
                }
                setGeolocating(false);
                setLocationErrorMessage(null);
                setSortMode("manualSearch");
              }}
              borderRadius="rounded"
              paddingX="2"
              paddingY="1"
              background={
                sortMode === "manualSearch" ? "primary" : "neutral-300"
              }
            >
              <Text
                color={
                  sortMode === "manualSearch" ? "overPrimary" : "neutral-900"
                }
                size="small"
                weight="regular"
              >
                Find
              </Text>
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => {
                setSortMode("geolocate");
                if (!userCoords) requestGeolocation();
              }}
              borderRadius="rounded"
              paddingX="2"
              paddingY="1"
              background={sortMode === "geolocate" ? "primary" : "neutral-300"}
            >
              <Text
                color={sortMode === "geolocate" ? "overPrimary" : "neutral-900"}
                size="small"
                weight="regular"
                whiteSpace="nowrap"
              >
                Near Me
              </Text>
            </Box>
          </Box>
        </Box>
        {sortMode === "manualSearch" && (
          <Box display="flex" gap="2" paddingBottom="2">
            <Box style={{ flex: 1 }}>
              <TextField
                highlightOnFocus
                placeholder="Enter City, State, or ZIP"
                value={inputValue}
                onChange={(value) => setInputValue(value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchSubmit();
                }}
                onClear={() => {
                  setInputValue("");
                  setSearch("");
                }}
                clearVisible={Boolean(inputValue)}
              />
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleSearchSubmit}
              disabled={searchDisabled}
              borderRadius="standard"
              paddingX="2"
              paddingY="1"
              background="primary"
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{
                flexShrink: 0,
                minWidth: 60,
                opacity: searchDisabled ? 0.45 : 1,
                cursor: searchDisabled ? "default" : "pointer",
              }}
            >
              {loadingFindPlace ? (
                <LoaderIcon />
              ) : (
                <Text color="overPrimary" size="small" weight="regular">
                  Search
                </Text>
              )}
            </Box>
          </Box>
        )}
      </Box>
      {geolocating && sortMode === "geolocate" && (
        <Box style={{ maxWidth: VIEWPORT_MD, margin: "0 auto", width: "100%" }}>
          <Box
            background="neutral-200"
            borderRadius="standard"
            paddingX="4"
            paddingBottom="2"
            display="flex"
            alignItems="center"
            gap="2"
          >
            <LoaderIcon />
            <Text size="small">Locating your position…</Text>
          </Box>
        </Box>
      )}
      {locationErrorMessage && sortMode === "geolocate" && (
        <Box style={{ maxWidth: VIEWPORT_MD, margin: "0 auto", width: "100%" }}>
          <Box
            background="neutral-200"
            borderRadius="standard"
            paddingX="4"
            paddingBottom="2"
          >
            <Text size="small" color="warning">
              {locationErrorMessage}
            </Text>
          </Box>
        </Box>
      )}
      {findPlaceError && sortMode === "manualSearch" && (
        <Box style={{ maxWidth: VIEWPORT_MD, margin: "0 auto", width: "100%" }}>
          <Box
            background="neutral-200"
            borderRadius="standard"
            paddingX="4"
            paddingBottom="2"
          >
            <Text size="small" color="warning">
              {`There was an error searching for "${search}". Try searching
              again or sort by nearby stores.`}
            </Text>
          </Box>
        </Box>
      )}
      <Divider />
      <Box
        background="neutral-200"
        flexGrow={1}
        display="flex"
        flexDirection="column"
      >
        <Box
          display="flex"
          flexDirection="column"
          flexGrow={1}
          paddingTop="1"
          style={{ maxWidth: VIEWPORT_MD, margin: "0 auto", width: "100%" }}
        >
          {loading ? (
            <>
              <LocationCard skeleton />
              <LocationCard skeleton />
              <LocationCard skeleton />
              <LocationCard skeleton />
              <LocationCard skeleton />
            </>
          ) : filteredSortedLocations.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              flexGrow={1}
              paddingX="2"
            >
              <Image
                src="/images/jetman_search.png"
                alt="Jet Man"
                width={250}
                height={250}
                style={{ objectFit: "contain" }}
              />
              <br />
              <Text align="center">{noResultsText}</Text>
            </Box>
          ) : (
            filteredSortedLocations.map((location) => {
              // No distance for a state search; otherwise distance from the
              // searched place (manualSearch) or the user's position (geolocate).
              let distance: number | null = null;
              if (!stateSearch) {
                distance =
                  sortMode === "manualSearch"
                    ? location.searchDistance
                    : location.userDistance;
              }
              return (
                <Link
                  key={location.ref}
                  href={`/${location.ref}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <LocationCard loc={location} distance={distance} />
                </Link>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}
